import React from "react";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";

interface ConfirmDialogProps {
  isOpen: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  color?: PaletteColor;
  type?: "normal" | "danger";
}

export function ConfirmDialog({
  isOpen,
  title = "確認",
  message,
  confirmText = "OK",
  cancelText = "キャンセル",
  onConfirm,
  onCancel,
  color = "Blue",
  type = "normal",
}: ConfirmDialogProps) {
  const isDarkMode = useDarkMode();

  if (!isOpen) return null;

  const confirmButtonStyle =
    type === "danger"
      ? {
          backgroundColor: "#EF4444",
        }
      : {
          backgroundColor: PaletteColorHex[color],
        };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`w-full max-w-sm rounded-lg shadow-xl p-6 ${
          isDarkMode ? "bg-gray-800 text-gray-200" : "bg-white text-gray-900"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className={`text-lg font-bold mb-3 ${
            isDarkMode ? "text-gray-100" : "text-gray-800"
          }`}
        >
          {title}
        </h3>
        <p
          className={`text-sm mb-6 whitespace-pre-wrap ${
            isDarkMode ? "text-gray-300" : "text-gray-600"
          }`}
        >
          {message}
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              isDarkMode
                ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors hover:brightness-110"
            style={confirmButtonStyle}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// カスタムフック: confirm の代わりに使用
export function useConfirmDialog() {
  const [dialogState, setDialogState] = React.useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    color: PaletteColor;
    type: "normal" | "danger";
    resolve: ((value: boolean) => void) | null;
  }>({
    isOpen: false,
    title: "確認",
    message: "",
    confirmText: "OK",
    cancelText: "キャンセル",
    color: "Blue",
    type: "normal",
    resolve: null,
  });

  const confirm = React.useCallback(
    (options: {
      title?: string;
      message: string;
      confirmText?: string;
      cancelText?: string;
      color?: PaletteColor;
      type?: "normal" | "danger";
    }): Promise<boolean> => {
      return new Promise((resolve) => {
        setDialogState({
          isOpen: true,
          title: options.title || "確認",
          message: options.message,
          confirmText: options.confirmText || "OK",
          cancelText: options.cancelText || "キャンセル",
          color: options.color || "Blue",
          type: options.type || "normal",
          resolve,
        });
      });
    },
    []
  );

  const handleConfirm = React.useCallback(() => {
    dialogState.resolve?.(true);
    setDialogState((prev) => ({ ...prev, isOpen: false, resolve: null }));
  }, [dialogState.resolve]);

  const handleCancel = React.useCallback(() => {
    dialogState.resolve?.(false);
    setDialogState((prev) => ({ ...prev, isOpen: false, resolve: null }));
  }, [dialogState.resolve]);

  const ConfirmDialogComponent = React.useMemo(
    () => (
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
        color={dialogState.color}
        type={dialogState.type}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    ),
    [dialogState, handleConfirm, handleCancel]
  );

  return { confirm, ConfirmDialogComponent };
}
